import React, { useState, useEffect } from 'react';
import AdvisorBonus from '@app/components/advisor_bonus';
import { Popover } from '@barges/react';
import { analyticsService } from '@common/analytics';
import classes from './classes.module.scss';
import DownloadAppPopup from './download_app_popup';
import { getSessionType } from '../../../actions/analytics';
import RateReviewAfterChat from '../../rate_review/rate_review_after_chat';
import { appRoutes } from '../../../config/app_routes';
import { SvgIcon } from '../../../../common/sprites';
import { useLocalizedNavigate } from '../../routes/hooks/useLocalizedNavigate';
import { useNotification } from '../../firebase_messaging_provider';

const STATE_NEED_REVIEW = 'need_review';

const getNeedShowReviewBrowser = ({ orderState }) => STATE_NEED_REVIEW === orderState;

function ChatEnded({
  cancelChat,
  t,
  clearAffiliatePromoPopup,
  clientSummaryScreen = {},
  orderState,
  name,
  orderId,
  advisorId,
  profileLanguage,
  createdAt,
  chatType,
  showDownloadAppPopupKind,
  askForAdvisorBonus,
  loading,
  clearSumamryScreen,
  clearOrderDetails
}) {
  const {
    title, alert, largeNumbers, smallNumbers, total, hangupError
  } = clientSummaryScreen || {};
  const [showAdvisorBonus, setShowAdvisorBonus] = useState(false);

  const history = useLocalizedNavigate();
  const [needShowReviewBrowser, setNeedShowReviewBrowser] = useState(getNeedShowReviewBrowser({ orderState }));
  const { requestPermission } = useNotification();

  useEffect(() => {
    analyticsService.trackPageVisit(`${ getSessionType(chatType) } ended`);
  }, []);

  const redirectTo = (needRedirect = false) => {
    if ((needRedirect || (!askForAdvisorBonus || showDownloadAppPopupKind)) && advisorId) {
      history(appRoutes.advisorOrders, { params: { id: advisorId } });
    }
  };

  const cancelAction = async () => {
    await setShowAdvisorBonus(askForAdvisorBonus);
    await clearAffiliatePromoPopup();
    if (askForAdvisorBonus && !showDownloadAppPopupKind) {
      await clearSumamryScreen();
    } else {
      await cancelChat();
      await clearOrderDetails();
    }
  };

  const getNeedShowAdvisorBonus = () => !showDownloadAppPopupKind && showAdvisorBonus && !needShowReviewBrowser;

  const cancelRateReviewBrowser = (like) => {
    clearAffiliatePromoPopup();
    setNeedShowReviewBrowser(false);
    redirectTo();
    if (like !== false) requestPermission();
  };

  const renderTitleBlock = () => (
    <div className={ classes.titleContainerBrowser }>
      <h4 data-testid="title-header">{title}</h4>
    </div>
  );

  const renderHangupErrorBlock = () => (
    hangupError && (
    <div className={ classes.hangupErrorBlock } data-testid="hangup-error-block">
      <div className={ classes.hangupError }>{hangupError?.label}</div>
    </div>
    )
  );

  const renderAlertBlock = () => (
    alert && (
    <div className={ classes.alertBlock }>
      <div className={ classes.alert }>{alert?.label}</div>
      <div className={ classes.alertHint }>{alert?.hint}</div>
    </div>
    )
  );

  const renderLargeNumbersBlock = () => (
    <div style={ { overflowY: 'auto', height: 300 } } className={ classes.chatInfoContainerBrowser }>
      {largeNumbers?.map((line, index) => (
        <div key={ index }>
          <span className={ classes.descriptionTextBrowser }>{ line?.label }</span>
          <br />
          <div className={ classes.descriptionDataBrowser }>
            {line?.value}
          </div>
        </div>
      ))}
    </div>
  );

  const renderSmallNumbersBlock = () => (
    smallNumbers?.length > 0 && (
      <div className={ classes.discountBlock }>
        {smallNumbers?.map((line, index) => (
          <div key={ index } className={ classes.smallBlockLine }>
            <div className={ classes.smallBlockLabel }>{line?.label}</div>
            <div className={ classes.smallBlockValue }>{line?.value}</div>
          </div>
        ))}
      </div>
    )
  );

  const renderTotalHint = () => {
    if (!total?.hint) return null;
    return (
      <div className={ classes.totalHint }>
        { total?.hint }
        <span className={ classes.totalHintHighlighted }>{ ` ${ total?.hintHighlighted }` }</span>
      </div>
    );
  };

  const renderTotalBlock = () => (
    <div className={ classes.totalWrapper }>
      <div className={ classes.totalLabel }>{total?.label}</div>
      <div className={ classes.totalValue }>{total?.value}</div>
      { renderTotalHint() }
    </div>
  );

  const closeButton = () => (
    <button
      data-testid="chat-ended-close-btn"
      onClick={ () => {
        cancelAction();
        redirectTo();
      } }
      type="button"
      className={ classes.closeAction }
    >
      <SvgIcon id="close_icon" size={ 28 } />
    </button>
  );

  const renderChatModalContentNumbers = () => (
    <>
      {renderTitleBlock()}
      {renderHangupErrorBlock()}
      {renderLargeNumbersBlock()}
      {renderSmallNumbersBlock()}
    </>
  );

  const renderChatEndedModalContentHeader = () => (
    <>
      {renderChatModalContentNumbers()}
      {renderTotalBlock()}
      {renderAlertBlock()}
      {closeButton()}
    </>
  );

  const renderChatEndedModal = () => (
    <Popover isOpen className={ classes.chatEndedPopupContainer }>
      <div className={ classes.container }>
        {renderChatEndedModalContentHeader()}
      </div>
      <div className={ classes.doneButtonContainerBrowser }>
        <button
          type="button"
          data-testid="continue-btn"
          className={ classes.doneBtnBrowser }
          onClick={ () => {
            cancelAction();
            redirectTo();
          } }
          onKeyDown={ () => {
            cancelAction();
            redirectTo();
          } }
        >
          {t('chat.continue')}
        </button>
      </div>
    </Popover>
  );

  const onCancelAdvisorBonus = async () => {
    setShowAdvisorBonus(false);
    await cancelChat();
    await clearOrderDetails();
    redirectTo(true);
  };

  const renderChatEnded = () => (
    <>
      { clientSummaryScreen?.title && !needShowReviewBrowser && renderChatEndedModal() }
      <RateReviewAfterChat
        visible={ needShowReviewBrowser }
        onCancel={ cancelRateReviewBrowser }
        advisorName={ name }
        orderId={ orderId }
        advisorId={ advisorId }
        profileLanguage= { profileLanguage }
        createdAt={ createdAt }
        clickSource = "chat ended"
        chatType={ chatType }
      />
      { !loading && getNeedShowAdvisorBonus() && <AdvisorBonus key="advisor-bonus-in-chat-ended" advisorId={ advisorId } isOpen={ getNeedShowAdvisorBonus() } orderId={ orderId } onCancel={ onCancelAdvisorBonus } clickSource="post live session" /> }
      <DownloadAppPopup />
    </>
  );

  return renderChatEnded();
}

export default ChatEnded;
